import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import styles from './GetFeatured.module.sass';
import InfoPage from '../InfoPage';
import { getAuthToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';
import { uploadFile } from '../../utils/fileUpload';
import { CheckCircle, Clock, XCircle, Star, Upload, Image } from 'lucide-react';
import BlurredGate from '../../components/BlurredGate';

const STATUS_CONFIG = {
  pending:  { label: 'Pending Review', color: '#92400e', bg: '#fef3c7', Icon: Clock },
  approved: { label: 'Approved',       color: '#065f46', bg: '#d1fae5', Icon: CheckCircle },
  featured: { label: 'Featured',       color: '#1e40af', bg: '#dbeafe', Icon: Star },
  declined: { label: 'Declined',       color: '#6b7280', bg: '#f3f4f6', Icon: XCircle },
};

const GetFeatured = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membershipTier, setMembershipTier] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const email = typeof window !== 'undefined' ? window.localStorage.getItem('postaway_email') || '' : '';
        if (email) {
          const res = await fetch('/api/access/subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          const data = await res.json();
          if (res.ok && data.ok) {
            setMembershipTier(data.membershipTier || 'all_access');
          }
        }
      } catch (_) {}
    })();
  }, []);

  // Form state
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [hostHandle, setHostHandle] = useState('');
  const [caption, setCaption] = useState('');
  const [hasPermission, setHasPermission] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4000' : '';

  const fetchSubmissions = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await authFetch(`${apiBase}/api/featured/my-submissions`);
      if (response.ok) {
        const data = await response.json();
        if (data.ok) setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image must be under 20MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type.toLowerCase()) && !file.name.toLowerCase().endsWith('.heic')) {
      setError('Please upload a JPG, PNG, or WebP image');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setImageUrl(url);
      setImagePreview(url);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!imageUrl) { setError('Please upload an image'); return; }
    if (!hasPermission) { setError('You must confirm you have permission to share this image'); return; }

    setError('');
    setSubmitting(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${apiBase}/api/featured/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          imageUrl,
          hostHandle: hostHandle.trim(),
          caption: caption.trim(),
          hasPermission,
        }),
      });

      const data = await response.json();
      if (data.ok) {
        setSubmitSuccess(true);
        setImageUrl('');
        setImagePreview('');
        setHostHandle('');
        setCaption('');
        setHasPermission(false);
        fetchSubmissions();
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setError(data.error || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit failed:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className={styles.page}>
      <div className={styles.container}>
        <p className={styles.subtitle}>
          Submit your best photos for a chance to be featured on the Postaway Instagram grid.
        </p>

        {/* Upload Form */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Submit a Photo</h2>

          {submitSuccess && (
            <div className={styles.successBanner}>
              <CheckCircle size={18} />
              Thanks! We are reviewing it and can't wait to share.
            </div>
          )}

          <div className={styles.form}>
            <div className={styles.uploadArea}>
              {imagePreview ? (
                <div className={styles.previewWrap}>
                  <img src={imagePreview} alt="Preview" className={styles.preview} />
                  <button
                    className={styles.removeBtn}
                    onClick={() => { setImageUrl(''); setImagePreview(''); }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className={cn(styles.uploadLabel, { [styles.uploading]: uploading })}>
                  <input
                    type="file"
                    accept="image/*,.heic"
                    onChange={handleFileChange}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                  {uploading ? (
                    <span>Uploading...</span>
                  ) : (
                    <>
                      <Upload size={32} />
                      <span>Click to upload an image</span>
                      <span className={styles.uploadHint}>JPG, PNG, WebP up to 20MB</span>
                    </>
                  )}
                </label>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Host Handle</label>
              <div className={styles.handleInput}>
                <span className={styles.handlePrefix}>@</span>
                <input
                  type="text"
                  value={hostHandle}
                  onChange={(e) => setHostHandle(e.target.value.replace(/@/g, ''))}
                  placeholder="host handle (who's property is in the photo?)"
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Caption (optional)</label>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Short description for Instagram context"
                className={styles.input}
                maxLength={200}
              />
            </div>

            <label className={styles.permissionCheck}>
              <input
                type="checkbox"
                checked={hasPermission}
                onChange={(e) => setHasPermission(e.target.checked)}
              />
              <span>
                I confirm I have permission to share this image and grant Postaway the right to
                feature it on social media.
              </span>
            </label>

            {error && <p className={styles.error}>{error}</p>}

            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={submitting || !imageUrl || !hasPermission}
            >
              {submitting ? 'Submitting...' : 'Submit Photo'}
            </button>
          </div>
        </div>

        {/* My Submissions */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>My Submissions</h2>

          {loading ? (
            <p className={styles.emptyText}>Loading...</p>
          ) : submissions.length === 0 ? (
            <div className={styles.empty}>
              <Image size={40} />
              <p>No submissions yet</p>
              <span>Upload a photo above to get started</span>
            </div>
          ) : (
            <div className={styles.grid}>
              {submissions.map((sub) => {
                const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                return (
                  <div key={sub.id} className={styles.card}>
                    <div className={styles.cardImage}>
                      <img src={sub.image_url} alt="Submission" />
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.cardMeta}>
                        {sub.host_handle && (
                          <span className={styles.cardHandle}>@{sub.host_handle}</span>
                        )}
                        <span className={styles.cardDate}>
                          {new Date(sub.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {sub.caption && <p className={styles.cardCaption}>{sub.caption}</p>}
                      <div
                        className={styles.statusBadge}
                        style={{ background: cfg.bg, color: cfg.color }}
                      >
                        <cfg.Icon size={14} />
                        {cfg.label}
                      </div>
                      {sub.status === 'declined' && sub.admin_note && (
                        <p className={styles.adminNote}>{sub.admin_note}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (membershipTier === 'basic') {
    const userType = (typeof window !== 'undefined' && window.localStorage.getItem('postaway_user_type')) || 'host';
    return (
      <InfoPage
        title="Get Featured"
        divider={false}
        below={
          <BlurredGate userType={userType}>
            {content}
          </BlurredGate>
        }
        disableStamps
        tallOverride={false}
        customMinHeight={undefined}
      />
    );
  }

  return (
    <InfoPage
      title="Get Featured"
      divider={false}
      below={content}
      disableStamps
      tallOverride={false}
      customMinHeight={undefined}
    />
  );
};

export default GetFeatured;
